import {
    createElement,
    useEffect,
    useLayoutEffect,
    useState,
    useRef,
} from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsivePie } from "@nivo/pie";
import ChartCardPopup from "./ChartCardPopup";
import { useNavigate } from "react-router-dom";
import { setSelectedReportViewId } from "../../slices/viewReportSlice";
import { useDispatch } from "react-redux";
import CustomLegend from "./charts/customLegend";
import html2canvas from "html2canvas";
import Tooltip from "../../components/ui/Tooltip";
import { EXPORT_IGNORE_ATTRIBUTE } from "../../hooks/useExportGraphsToPptx";

const needsPercentageNote = (groups) =>
    groups.some((group) => {
        const values = group.filter(
            (value) => typeof value === "number" && Number.isFinite(value)
        );
        const total = values.reduce((sum, value) => sum + value, 0);
        return (
            values.some((value) => value > 0 && value < 1) ||
            (total > 0 && Math.abs(total - 100) > 0.000001)
        );
    });

const ChartTooltipOverlay = ({ children, position }) => {
    const tooltipRef = useRef(null);
    const [placement, setPlacement] = useState(null);
    const [hidden, setHidden] = useState(false);

    useLayoutEffect(() => {
        if (!position || !tooltipRef.current) return;
        const { width, height } = tooltipRef.current.getBoundingClientRect();
        const gap = 12;
        setPlacement({
            left: Math.max(
                8,
                Math.min(position.x + gap, window.innerWidth - width - 8)
            ),
            top: Math.max(
                8,
                Math.min(
                    position.y - height - gap >= 8
                        ? position.y - height - gap
                        : position.y + gap,
                    window.innerHeight - height - 8
                )
            ),
        });
        setHidden(false);
    }, [position]);

    useEffect(() => {
        const hide = () => setHidden(true);
        window.addEventListener("scroll", hide, true);
        return () => window.removeEventListener("scroll", hide, true);
    }, []);

    return createPortal(
        <div
            ref={tooltipRef}
            role="tooltip"
            style={{
                position: "fixed",
                ...placement,
                visibility: !placement || hidden ? "hidden" : "visible",
                pointerEvents: "none",
                zIndex: 1100,
                maxWidth: "calc(100vw - 16px)",
            }}
        >
            {children}
        </div>,
        document.body
    );
};

ChartTooltipOverlay.propTypes = {
    children: PropTypes.node,
    position: PropTypes.shape({ x: PropTypes.number, y: PropTypes.number }),
};

// Centered label inside a donut chart
const CenteredMetric = ({
    centerX,
    centerY,
    label,
    pieTotalRecords,
    disabilitySnapshot,
}) => {
    const words = label.split(" ");
    const lineHeight = 1.4;
    const totalHeight = (words.length - 1) * lineHeight;

    return (
        <text
            x={centerX}
            y={centerY}
            textAnchor="middle"
            dominantBaseline="middle"
            style={{
                // fontSize: 12.8,
                fontWeight: 400,
                fill: "#525252",
            }}
        >
            {disabilitySnapshot ? (
                <>
                    <tspan x={centerX} style={{ fontSize: 25 }}>
                        {pieTotalRecords}
                    </tspan>
                    <tspan x={centerX} dy="2em" style={{ fontSize: 12.8 }}>
                        Claims
                    </tspan>
                </>
            ) : (
                words.map((word, i) => (
                    <tspan
                        key={`${word}-${i}`}
                        x={centerX}
                        dy={
                            i === 0
                                ? -totalHeight / 2 + "em"
                                : lineHeight + "em"
                        }
                    >
                        {word}
                    </tspan>
                ))
            )}
        </text>
    );
};
CenteredMetric.propTypes = {
    centerX: PropTypes.number,
    centerY: PropTypes.number,
    label: PropTypes.string,
};

// Custom component for centered metric in donut chart
const CustomCenteredMetric =
    ({ label, pieTotalRecords, disabilitySnapshot }) =>
    (props) => (
        <CenteredMetric
            {...props}
            label={label}
            pieTotalRecords={pieTotalRecords}
            disabilitySnapshot={disabilitySnapshot}
        />
    );

// Individual Donut chart container
const PieChartCenterText = ({
    options,
    label,
    pieTotalRecords,
    disabilitySnapshot,
}) => {
    return (
        <div className="donut-chart" style={{ width: "100%", height: 440 }}>
            <ResponsivePie
                {...options}
                layers={[
                    "arcs",
                    "arcLabels",
                    "arcLinkLabels",
                    "legends",
                    CustomCenteredMetric({
                        label,
                        pieTotalRecords,
                        disabilitySnapshot,
                    }),
                ]}
            />
        </div>
    );
};

PieChartCenterText.propTypes = {
    options: PropTypes.object.isRequired,
    label: PropTypes.string.isRequired,
};

const ChartCard = ({
    title,
    description,
    selectedCompanyYourCompanyTo,
    selectedFilters = {
        selectedCoverageType: "",
        selectedReportingGroup: [],
    },
    variant,
    groupBy,
    chartOptions,
    labels = [],
    isRotate = false,
    errorMessage,
    disabilitySnapshot,
    totalRecords,
    viewId,
    pieTotalRecords,
    reportDetails,
    comparisons,
    legendOffsetY,
    disclaimer,
    cacheKey,
    clientClaimCount,
    chartHeight,
    chartMinWidth,
    onToggleGrouping,
    groupingSwapped = false,
    viewControls,
    percentageGroups = [],
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [showPopup, setShowPopup] = useState(false);
    const [selectedHyperLink, setSelectedHyperLink] = useState("");
    const sectionRef = useRef(null);
    const tooltipPositionRef = useRef(null);

    const trackTooltipPointer = (event) => {
        tooltipPositionRef.current = {
            x: event.clientX,
            y: event.clientY,
        };
    };

    const handleClick = async () => {
        try {
            if (!sectionRef.current) {
                console.error(
                    "Section card ref not found for screenshot capture."
                );
                return;
            }

            const canvas = await html2canvas(sectionRef.current, {
                backgroundColor: "#ffffff",
                useCORS: true,
                scale: 2,
            });

            const imageData = canvas.toDataURL("image/png");

            dispatch(setSelectedReportViewId(viewId));
            navigate(`/dashboard/detailed-report`, {
                state: {
                    viewId,
                    selectedView: reportDetails?.find(
                        (view) => view.ViewId === viewId
                    ),
                    chartScreenshot: imageData,
                    disclaimer,
                    cacheKey,
                },
            });
        } catch (error) {
            console.error("Error capturing screenshot:", error);
        }
    };

    const handleOpenModal = (value) => {
        setShowPopup(true);
        setSelectedHyperLink(value);
    };

    const handleCloseModal = () => {
        setShowPopup(false);
    };

    useEffect(() => {
        document.body.style.overflow = showPopup ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [showPopup]);

    const isIndustryPeers = (selectedCompanyYourCompanyTo ?? [])
        .map((item) => item.value)
        .includes("industryPeers");

    // --------------------------------------------------------------------
    // Resuable Helpers
    // --------------------------------------------------------------------

    const renderBarChart = () => {
        if (errorMessage) {
            return (
                <div className="d-flex no-data-message">
                    <div className="d-flex" style={{ gap: "8px" }}>
                        <i className="d-flex align-items-center fa-solid fa-circle-exclamation warning-color"></i>
                        <div
                            className="infobanner-text"
                            dangerouslySetInnerHTML={{ __html: errorMessage }}
                        ></div>
                    </div>
                </div>
            );
        }

        return (
            <div
                className="chart-container"
                style={{
                    height:
                        groupBy === "year" ||
                        groupBy === "quarter" ||
                        !disabilitySnapshot
                            ? "auto"
                            : chartHeight
                              ? chartHeight + 50
                              : isRotate
                                ? 682
                                : disabilitySnapshot
                                  ? 400
                                  : 450,
                    zIndex: 1,
                }}
            >
                <div
                    onMouseOverCapture={trackTooltipPointer}
                    onMouseMoveCapture={trackTooltipPointer}
                    onFocusCapture={(event) => {
                        const rect = event.target.getBoundingClientRect();
                        tooltipPositionRef.current = {
                            x: rect.left + rect.width / 2,
                            y: rect.top,
                        };
                    }}
                    style={{
                        overflowX: chartMinWidth ? "auto" : undefined,
                        height: chartMinWidth
                            ? (chartHeight || 400) + 20
                            : chartHeight
                              ? chartHeight
                              : !disabilitySnapshot
                                ? 400
                                : "100%",
                    }}
                >
                    {chartMinWidth ? (
                        <div
                            style={{
                                minWidth: chartMinWidth,
                                height: chartHeight || 400,
                            }}
                        >
                            <ResponsiveBar
                                key={
                                    groupBy === "year" || groupBy === "quarter"
                                        ? chartOptions.indexBy
                                        : undefined
                                }
                                {...chartOptions}
                                {...(typeof chartOptions.tooltip === "function"
                                    ? {
                                          tooltip: (datum) => (
                                              <ChartTooltipOverlay
                                                  position={
                                                      tooltipPositionRef.current
                                                  }
                                              >
                                                  {createElement(
                                                      chartOptions.tooltip,
                                                      datum
                                                  )}
                                              </ChartTooltipOverlay>
                                          ),
                                      }
                                    : {})}
                            />
                        </div>
                    ) : (
                        <ResponsiveBar
                            key={
                                groupBy === "year" || groupBy === "quarter"
                                    ? chartOptions.indexBy
                                    : undefined
                            }
                            {...chartOptions}
                        />
                    )}
                </div>

                {!disabilitySnapshot && (
                    <CustomLegend
                        comparisons={comparisons}
                        legendOffsetY={legendOffsetY}
                        compact
                        isRotate={isRotate}
                    />
                )}
            </div>
        );
    };

    const renderDonutChart = () => {
        if (errorMessage) {
            return (
                <div className="d-flex no-data-message">
                    <div className="d-flex" style={{ gap: "8px" }}>
                        <i className="d-flex align-items-center fa-solid fa-circle-exclamation warning-color"></i>
                        <div
                            className="infobanner-text"
                            dangerouslySetInnerHTML={{ __html: errorMessage }}
                        ></div>
                    </div>
                </div>
            );
        }

        return (
            <div
                className={
                    groupBy === "year" || groupBy === "quarter"
                        ? "donut-group donut-group--temporal"
                        : "donut-group"
                }
            >
                {Array.isArray(chartOptions) && chartOptions.length > 0 ? (
                    chartOptions.map((opts, idx) => {
                        const uniqueKey =
                            opts.id || opts.label || labels[idx] || idx;
                        return (
                            <PieChartCenterText
                                key={uniqueKey}
                                options={opts}
                                label={labels[idx] || ""}
                                pieTotalRecords={pieTotalRecords}
                                disabilitySnapshot={disabilitySnapshot}
                            />
                        );
                    })
                ) : (
                    <PieChartCenterText
                        options={chartOptions}
                        label={labels[0] || ""}
                        pieTotalRecords={pieTotalRecords}
                        disabilitySnapshot={disabilitySnapshot}
                    />
                )}
            </div>
        );
    };

    const {
        selectedCoverageType,
        selectedReportingGroup,
        selectedPolicyDivision,
    } = selectedFilters;
    const isLeaveCoverageType = selectedCoverageType === "Leave";

    return (
        <div
            className={`section-card px-0 pt-0 ${disabilitySnapshot ? "mb-0" : ""}`}
            data-chart-min-width={chartMinWidth}
        >
            <div style={{ padding: "24px 24px 0px 24px" }} ref={sectionRef}>
                {!errorMessage && viewControls}
                {onToggleGrouping && !errorMessage && (
                    <button
                        type="button"
                        className="chart-grouping-toggle float-end"
                        onClick={onToggleGrouping}
                        aria-label="Swap chart grouping"
                        aria-pressed={groupingSwapped}
                        {...{ [EXPORT_IGNORE_ATTRIBUTE]: "true" }}
                        title={
                            groupBy === "year" || groupBy === "quarter"
                                ? "Swap categories and periods"
                                : "Swap categories and comparisons"
                        }
                        style={{
                            width: 36,
                            height: 36,
                            padding: 0,
                            marginLeft: 12,
                        }}
                    >
                        <i
                            className="fa-solid fa-right-left"
                            aria-hidden="true"
                        />
                    </button>
                )}
                <h3 className="chart-title">
                    {title}{" "}
                    {disabilitySnapshot && totalRecords && (
                        <span>({totalRecords} claims)</span>
                    )}
                </h3>
                <p className="chart-subtitle">{description} </p>

                {!errorMessage && (
                    <>
                        {!disabilitySnapshot && (
                            <>
                                <div
                                    className="d-flex gap-2 dashboard-hyper-links"
                                    {...{ [EXPORT_IGNORE_ATTRIBUTE]: "true" }}
                                >
                                    {isIndustryPeers && (
                                        <>
                                            <button
                                                type="button"
                                                style={{
                                                    border: "none",
                                                    backgroundColor: "#fff",
                                                }}
                                                className="hyper-links"
                                                onClick={() =>
                                                    handleOpenModal("peers")
                                                }
                                            >
                                                View industry peers
                                            </button>
                                            <div
                                                className="vertical-divider"
                                                style={{ margin: 0 }}
                                            />
                                        </>
                                    )}
                                    <button
                                        type="button"
                                        style={{
                                            border: "none",
                                            backgroundColor: "#fff",
                                        }}
                                        className="hyper-links"
                                        onClick={() =>
                                            handleOpenModal(
                                                isLeaveCoverageType
                                                    ? "reportingGroups"
                                                    : "policies"
                                            )
                                        }
                                    >
                                        View your{" "}
                                        {isLeaveCoverageType
                                            ? "reporting groups"
                                            : "policies"}
                                    </button>
                                </div>
                                <div className="client-claim-count-info">
                                    <span className="client-claim-count-text">
                                        Client
                                        {isLeaveCoverageType
                                            ? " leave "
                                            : " claim "}
                                        count:{" "}
                                        {clientClaimCount.toLocaleString(
                                            "en-US"
                                        )}
                                    </span>
                                    <Tooltip
                                        content={
                                            <>
                                                The total number of your
                                                organization&#39;s{" "}
                                                {isLeaveCoverageType
                                                    ? "leaves"
                                                    : "claims"}{" "}
                                                included in this chart, based on
                                                the selected filters and
                                                reporting period. Client
                                                percentages are calculated using
                                                this count.
                                            </>
                                        }
                                        tooltipId="client-claim-count-tooltip"
                                        tooltipClassName="reporting-dropdown-tooltip"
                                        arrowClassName="reportingTooltipArrow"
                                        portal
                                        icon
                                        iconAriaLabel="More information about client claim count"
                                        iconColor="#015294"
                                        iconButtonStyle={{
                                            background: "transparent",
                                            border: 0,
                                            padding: "0 0 0 4px",
                                        }}
                                    />
                                </div>
                            </>
                        )}
                    </>
                )}
                <div
                    className="chart-content"
                    data-testid="chart-content"
                    style={{
                        height:
                            errorMessage ||
                            (!disabilitySnapshot && variant === "bar") ||
                            groupBy === "year" ||
                            groupBy === "quarter"
                                ? "auto"
                                : chartHeight
                                  ? chartHeight + 50
                                  : disabilitySnapshot && !errorMessage
                                    ? 370
                                    : "auto",
                    }}
                >
                    {variant === "bar" && renderBarChart()}
                    {variant === "donut" && renderDonutChart()}
                </div>

                {!errorMessage && needsPercentageNote(percentageGroups) && (
                    <p
                        className="chart-percentage-note"
                        style={{
                            color: "#525252",
                            backgroundColor: "#FFFFFF",
                            fontSize: 12,
                            lineHeight: 1.5,
                            margin: "8px 0 16px",
                        }}
                    >
                        {
                            "Percentages may not total 100% due to rounding. Small percentage values are shown as <1%."
                        }
                    </p>
                )}

                {showPopup && (
                    <div
                        className="chart-card-modal-overlay"
                        role="dialog"
                        aria-modal="true"
                        tabIndex={-1}
                        onClick={(e) => {
                            if (
                                e.target.classList.contains(
                                    "chart-card-modal-overlay"
                                )
                            ) {
                                handleCloseModal();
                            }
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Escape") {
                                handleCloseModal();
                            }
                        }}
                    >
                        <ChartCardPopup
                            selectedHyperLink={selectedHyperLink}
                            handleCloseModal={handleCloseModal}
                            selectedReportingGroup={selectedReportingGroup}
                            selectedPolicyDivision={selectedPolicyDivision}
                        />
                    </div>
                )}
            </div>

            {!errorMessage && disabilitySnapshot && (
                <span
                    className="a-self viewButton d-flex"
                    style={{
                        position: "relative",
                        zIndex: 2,
                        justifyContent: "flex-end",
                        paddingRight: "24px",
                        paddingTop: "24px",
                    }}
                >
                    <button
                        type="button"
                        className="d-flex button viewDetailButton"
                        onClick={handleClick}
                    >
                        View details
                        <i className="fa-solid fa-chevron-right ms-2"></i>
                    </button>
                </span>
            )}
        </div>
    );
};

ChartCard.propTypes = {
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    selectedCompanyYourCompanyTo: PropTypes.array,
    selectedFilters: PropTypes.object,
    accessibilityText: PropTypes.string,
    variant: PropTypes.oneOf(["bar", "donut"]),
    groupBy: PropTypes.string,
    chartOptions: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.arrayOf(PropTypes.object),
    ]),
    labels: PropTypes.arrayOf(PropTypes.string),
    isRotate: PropTypes.bool,
    errorMessage: PropTypes.string,
    disabilitySnapshot: PropTypes.bool,
    totalRecords: PropTypes.number,
    viewId: PropTypes.number,
    pieTotalRecords: PropTypes.number,
    reportDetails: PropTypes.array,
    comparisons: PropTypes.array,
    legendOffsetY: PropTypes.number,
    disclaimer: PropTypes.string,
    cacheKey: PropTypes.string,
    clientClaimCount: PropTypes.number,
    chartHeight: PropTypes.number,
    chartMinWidth: PropTypes.number,
    onToggleGrouping: PropTypes.func,
    groupingSwapped: PropTypes.bool,
    viewControls: PropTypes.node,
    percentageGroups: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
};

export default ChartCard;
